// libraries
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from 'react-bootstrap';
import _debounce from 'lodash/debounce';
import _isNaN from 'lodash/isNaN';
// actions
import ShowNotification from '../../../actions/notification';
// constants
import { NOTIFICATION_TYPE } from '../../../constants/constants';
import { SETTINGS } from "../../../constants/administration";
// Components
import { withRouter } from '../../shared/WithRouter';
// services
import { getSetting, getWorkloadData, updateSetting } from '../../../services/administration';
// views
import LoadingBlock from '../../menu/LoadingBlock';

export class Workload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      workloadData: {},
      tenantCallCap: 0,
    };

    this.promises = {};

    this.assign = this.assign.bind(this);
    this.setCallCap = this.setCallCap.bind(this);
    this.getWorkloadData = this.getWorkloadData.bind(this);
    this.handleDebounceCallCap = _debounce(this.updateCallCap, 500);
  }

  componentDidMount() {
    this.getWorkloadData();
    this.getCallCap();
  }

  componentWillUnmount() {
    Object.keys(this.promises).forEach((key) => {
      this.promises[key].cancel();
    });
    this.handleDebounceCallCap.cancel();
  }

  getWorkloadData() {
    const { showNotification } = this.props;
    const promiseName = 'getWorkloadData';
    const getWorkloadDataRequest = getWorkloadData();

    const getWorkloadDataPromise = getWorkloadDataRequest.promise;
    this.promises[promiseName] = getWorkloadDataRequest;

    getWorkloadDataPromise.then((data) => {
      delete this.promises[promiseName];

      this.setState({
        workloadData: data,
        loading: false,
      });
    }).catch((error) => {
      delete this.promises[promiseName];

      if (error.status === 401 || error.status === 403 || error.isCanceled) {
        return;
      }

      this.setState({
        loading: false,
      });

      showNotification({
        message: 'Could not load workload data.',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  }

  getCallCap() {
    const { showNotification } = this.props;
    const settingServiceRequest = getSetting(SETTINGS.TENANT_CALL_CAP);
    const settingServicePromise = settingServiceRequest.promise;

    settingServicePromise.then((data) => {
      delete settingServiceRequest.promise;
      this.setState({ loading: false });
      const callCap = data?.length > 0 && data[0].value;
      this.setState((prevState) => ({
        ...prevState,
        tenantCallCap: callCap,
      }));
    }).catch((error) => {
      delete settingServiceRequest.promise;

      if (error.status === 401 || error.status === 403 || error.isCanceled) {
        return;
      }

      this.setState({ loading: false });

      const msgError = error.data && error.data.errors && error.data.errors.value;

      showNotification({
        message: msgError || 'An error occurred while attempting to get the tenant cap.',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  }

  setCallCap(number) {
    const validValue = !_isNaN(number) ? number : 0;
    const value = Math.max(0, Math.min(99, Number(validValue)));

    this.setState((prevState) => ({
      ...prevState,
      tenantCallCap: value,
    }), () => { this.handleDebounceCallCap(value); });
  }

  updateCallCap(value) {
    const { showNotification } = this.props;
    const settingServiceRequest = updateSetting(SETTINGS.TENANT_CALL_CAP, value);
    const settingServicePromise = settingServiceRequest.promise;

    settingServicePromise.then(() => {
      delete settingServiceRequest.promise;
      this.setState({ loading: false });
      showNotification({
        message: 'Successful tenant cap update',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.SUCCESS,
      });
      const { onCallCapUpdated } = this.props;
      if (onCallCapUpdated) {
        onCallCapUpdated(value);
      }
    }).catch((error) => {
      delete settingServiceRequest.promise;

      if (error.status === 401 || error.status === 403 || error.isCanceled) {
        return;
      }

      this.setState({ loading: false });

      const msgError = error.data && error.data.errors && error.data.errors.value;

      showNotification({
        message: msgError || 'An error occurred while attempting to update the tenant cap.',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  }

  assign() {
    const { navigate, params: { tenant: tenantUrl } } = this.props;
    navigate(`/${tenantUrl}/cn/list`);
  }

  render() {
    let content;
    const {
      loading, workloadData, tenantCallCap,
    } = this.state;

    if (loading) {
      content = <LoadingBlock />;
    } else {
      content = (
        <div className="flex-container workload">
          <div className="box workload-box">
            <div className="flex-container workload-row">
              <div>Patients - Total:</div>
              <div>{workloadData.totalOfConnectedToTheSystem}</div>
            </div>
            <div className="flex-container workload-row">
              <div>Patients - Assigned:</div>
              <div>{workloadData.totalOfAssignedToCn}</div>
            </div>
            <div className="flex-container workload-row">
              <div>Patients - Not Assigned:</div>
              <div>{workloadData.totalOfNotAssignedToCn}</div>
            </div>
            <div className="flex-container workload-row">
              <div>Tenant Call Cap:</div>
              <Form.Control
                type="number"
                value={Number(tenantCallCap).toString()}
                className="w-25 float-right"
                data-test="workload_inputtenantCallCap"
                onChange={(e) => this.setCallCap(e.target.value)}
              />
            </div>
          </div>
          <div className="box workload-box flex-container">
            <button
              type="button"
              className="button action-button h-50"
              onClick={this.assign}
            >
              Assign
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="admin-section my-4"
        id="workload"
      >
        <h5 className="text-uppercase my-3">Workload</h5>
        <div className="content workload-content">
          {content}
        </div>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    showNotification: notificationData => dispatch(ShowNotification(notificationData)),
  };
}

export default withRouter(connect(null, mapDispatchToProps, null, { forwardRef: true })(Workload));
